<template>
  <div class="dashboard">
    <!-- 统计卡片 -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <!-- 用户总数 - 仅管理员可见 -->
      <el-col :span="6" v-if="currentUser.role === 'admin'">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon user-icon">
              <el-icon><User /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-number">{{ stats.users }}</div>
              <div class="stat-label">用户总数</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <!-- 普通用户时调整列宽 -->
      <el-col :span="currentUser.role === 'admin' ? 6 : 8">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon playlist-icon">
              <el-icon><List /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-number">{{ stats.playlists }}</div>
              <div class="stat-label">{{ currentUser.role === 'admin' ? '歌单总数' : '我的歌单' }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="currentUser.role === 'admin' ? 6 : 8">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon song-icon">
              <el-icon><Headset /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-number">{{ stats.songs }}</div>
              <div class="stat-label">{{ currentUser.role === 'admin' ? '歌曲总数' : '我的歌曲' }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="currentUser.role === 'admin' ? 6 : 8">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon device-icon">
              <el-icon><Monitor /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-number">{{ stats.devices }}</div>
              <div class="stat-label">{{ currentUser.role === 'admin' ? '设备总数' : '我的设备' }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 公告区域 -->
    <el-card v-if="announcements.length > 0" class="announcement-card" style="margin-bottom: 20px;">
      <template #header>
        <div class="card-header">
          <span class="card-title">
            <el-icon><Bell /></el-icon>
            系统公告
          </span>
          <el-badge :value="announcements.length" class="announcement-badge" />
        </div>
      </template>
      <div class="announcement-list">
        <div
          v-for="announcement in announcements"
          :key="announcement.id"
          class="announcement-item"
          :class="{ 'pinned': announcement.is_pinned }"
        >
          <div class="announcement-header">
            <el-tag v-if="announcement.is_pinned" type="danger" size="small" class="pinned-tag">
              置顶
            </el-tag>
            <span class="announcement-title">{{ announcement.title }}</span>
            <span class="announcement-time">{{ formatTime(announcement.publish_at) }}</span>
          </div>
          <div 
            class="announcement-content" 
            :class="{ 'collapsed': !announcement.expanded && shouldCollapse(announcement.content) }"
            v-html="formatContent(announcement.content)"
          ></div>
          <div 
            v-if="shouldCollapse(announcement.content)" 
            class="announcement-expand"
            @click="toggleExpand(announcement)"
          >
            {{ announcement.expanded ? '收起' : '展开' }}
          </div>
        </div>
      </div>
    </el-card>

    <!-- 最近活动 / 系统状态（仅管理员可见） -->
    <el-row v-if="currentUser.role === 'admin'" :gutter="20">
      <el-col :span="12">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>最近登录用户</span>
            </div>
          </template>
          <div v-if="recentLogins.length > 0">
            <div
              v-for="login in recentLogins"
              :key="login.id"
              class="activity-item"
            >
              <el-avatar :size="32" style="margin-right: 10px;">
                {{ login.username?.charAt(0)?.toUpperCase() }}
              </el-avatar>
              <div class="activity-info">
                <div class="activity-title">{{ login.username }}</div>
                <div class="activity-time">{{ formatTime(login.timestamp) }}</div>
              </div>
            </div>
          </div>
          <el-empty v-else description="暂无记录" />
        </el-card>
      </el-col>
      <!-- 系统状态 -->
      <el-col :span="12">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>系统状态</span>
            </div>
          </template>
          <div class="system-status">
            <div class="status-item">
              <span class="status-label">API服务</span>
              <el-tag type="success">正常</el-tag>
            </div>
            <div class="status-item">
              <span class="status-label">数据库</span>
              <el-tag type="success">正常</el-tag>
            </div>
            <div class="status-item">
              <span class="status-label">文件存储</span>
              <el-tag type="success">正常</el-tag>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Bell } from '@element-plus/icons-vue';
import { getDashboardStats, getRecentActivity } from '../api/dashboard';
import { getAnnouncements } from '../api/announcement';

export default {
  name: 'DashboardPage',
  setup() {
    const stats = ref({
      users: 0,
      playlists: 0,
      songs: 0,
      devices: 0
    });

    const recentLogins = ref([]);
    const announcements = ref([]);
    
    // 获取当前用户信息
    const currentUser = ref({
      username: '',
      role: ''
    });

    const loadStats = async () => {
      try {
        const response = await getDashboardStats();
        if (response.success) {
          stats.value = response.data;
        } else {
          ElMessage.error(response.message || '加载统计数据失败');
        }
      } catch (error) {
        console.error('加载统计数据失败:', error);
        ElMessage.error('加载统计数据失败');
      }
    };

    const loadRecentLogins = async () => {
      try {
        const response = await getRecentActivity();
        if (response.success) {
          recentLogins.value = response.data || [];
        } else {
          console.error('加载最近登录记录失败:', response.message);
        }
      } catch (error) {
        console.error('加载最近登录记录失败:', error);
      }
    };

    const formatTime = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleString('zh-CN');
    };

    const loadAnnouncements = async () => {
      try {
        const response = await getAnnouncements({ page: 1, page_size: 5 });
        if (response.success) {
          // 初始化展开状态
          announcements.value = (response.data || []).map(item => ({
            ...item,
            expanded: false
          }));
        } else {
          console.error('加载公告失败:', response.message);
        }
      } catch (error) {
        console.error('加载公告失败:', error);
      }
    };

    const shouldCollapse = (content) => {
      if (!content) return false;
      // 如果内容超过100个字符，需要折叠
      return content.length > 100;
    };

    const formatContent = (content) => {
      if (!content) return '';
      // 简单的文本格式化，将换行符转换为<br>
      return content.replace(/\n/g, '<br>');
    };

    const toggleExpand = (announcement) => {
      announcement.expanded = !announcement.expanded;
    };

    onMounted(() => {
      // 获取当前用户信息
      let user = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr && userStr !== 'undefined') {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        user = {};
      }
      currentUser.value = user;
      
      loadStats();
      loadRecentLogins();
      loadAnnouncements();
    });

    return {
      stats,
      recentLogins,
      announcements,
      currentUser,
      formatTime,
      Bell,
      shouldCollapse,
      formatContent,
      toggleExpand
    };
  }
};
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

.stat-card {
  height: 120px;
}

.stat-content {
  display: flex;
  align-items: center;
  height: 100%;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 24px;
  color: white;
}

.user-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.playlist-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.song-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.device-icon {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-info {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-info {
  flex: 1;
}

.activity-title {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.activity-time {
  font-size: 12px;
  color: #999;
}

.system-status {
  padding: 10px 0;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.status-item:last-child {
  border-bottom: none;
}

.status-label {
  font-weight: 500;
  color: #333;
}

.announcement-card {
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.announcement-badge {
  margin-left: 10px;
}

.announcement-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.announcement-item {
  padding: 16px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background: #fff;
  transition: all 0.3s;
}

.announcement-item:hover {
  background: #F5F7FA;
  border-color: #C0C4CC;
}

.announcement-item.pinned {
  background: #FFF7E6;
  border-left: 3px solid #F56C6C;
}

.announcement-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.pinned-tag {
  flex-shrink: 0;
}

.announcement-title {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  color: #303133;
}

.announcement-time {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

.announcement-content {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  word-break: break-word;
}

.announcement-content.collapsed {
  max-height: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.announcement-expand {
  margin-top: 8px;
  font-size: 12px;
  color: #409EFF;
  cursor: pointer;
  text-align: right;
}

.announcement-expand:hover {
  color: #66B1FF;
}
</style> 